import type { AutomationActionParam, AutomationEntity, AutomationFieldType } from './automationApi'

/**
 * Черновик правила автосценария и его перевод в тело запроса и обратно (тикет #116).
 *
 * Конструктор умеет два уровня: корневую группу «все из / любое из» и вложенные в неё группы
 * условий. Больше уровней и отрицание (`not`) сервер понимает, а конструктор — нет: такое
 * правило открывается текстом JSON, а не пересобирается в похожее. Пересборка «примерно
 * так же» молча меняла бы смысл правила, которое кто-то написал руками.
 *
 * Плоская карта до #115 (`{"stage": "won"}`) открывается группой «все из» с равенствами и
 * при сохранении становится выражением: сервер сравнивает такие равенства по типу поля, а не
 * текстом, — для строк и идентификаторов это одно и то же.
 */

export interface CondLeaf {
  kind: 'leaf'
  key: string
  field: string
  op: string
  /** Как ввёл администратор; для «одно из» — через запятую. */
  value: string
}

export interface CondGroup {
  kind: 'group'
  key: string
  mode: 'all' | 'any'
  items: (CondLeaf | CondGroup)[]
}

export type ConditionsDraft = { root: CondGroup; raw?: undefined } | { raw: string; root?: undefined }

export interface TimerDraft {
  anchor: string
  direction: 'after' | 'before'
  amount: string
  unit: 'M' | 'H' | 'D'
  /** Срок, который не раскладывается на «число и единицу» (P1DT2H), — как есть. */
  custom: string | null
}

export interface ActionDraft {
  key: string
  type: string
  params: Record<string, string>
}

export const NO_VALUE_OPS = new Set(['empty', 'not_empty', 'changed'])
export const LIST_OPS = new Set(['in', 'not_in'])
export const PREVIOUS = 'previous.'

let counter = 0
export const nextKey = () => `k${++counter}`

export const emptyGroup = (mode: 'all' | 'any' = 'all'): CondGroup => ({ kind: 'group', key: nextKey(), mode, items: [] })

const isObject = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === 'object' && !Array.isArray(value)

const isLeafJson = (value: Record<string, unknown>) => 'field' in value && 'op' in value

function valueText(value: unknown): string {
  if (value == null) return ''
  if (Array.isArray(value)) return value.map((item) => valueText(item)).join(', ')
  return String(value)
}

function leafOf(json: Record<string, unknown>): CondLeaf {
  return { kind: 'leaf', key: nextKey(), field: String(json.field ?? ''), op: String(json.op ?? 'eq'), value: valueText(json.value) }
}

function groupOf(json: unknown, depth: number): CondGroup | null {
  if (!isObject(json)) return null
  const keys = Object.keys(json)
  const mode = keys.length === 1 && (keys[0] === 'all' || keys[0] === 'any') ? keys[0] : null
  if (!mode || !Array.isArray(json[mode])) return null
  const items: (CondLeaf | CondGroup)[] = []
  for (const item of json[mode] as unknown[]) {
    if (isObject(item) && isLeafJson(item)) {
      items.push(leafOf(item))
      continue
    }
    const nested = depth === 0 ? groupOf(item, 1) : null
    if (!nested) return null
    items.push(nested)
  }
  return { kind: 'group', key: nextKey(), mode, items }
}

/** Условия правила — в черновик конструктора, или текстом, если конструктор их не выразит. */
export function draftConditions(json: unknown): ConditionsDraft {
  if (json == null || (isObject(json) && Object.keys(json).length === 0)) return { root: emptyGroup() }
  if (isObject(json)) {
    if (isLeafJson(json)) return { root: { ...emptyGroup(), items: [leafOf(json)] } }
    const group = groupOf(json, 0)
    if (group) return { root: group }
    const keys = Object.keys(json)
    const special = keys.length === 1 && ['all', 'any', 'not'].includes(keys[0] ?? '')
    const flat = !special && Object.values(json).every((value) => value == null || typeof value !== 'object')
    if (flat) {
      return {
        root: {
          ...emptyGroup(),
          items: Object.entries(json).map(([field, value]) => ({ kind: 'leaf', key: nextKey(), field, op: 'eq', value: valueText(value) })),
        },
      }
    }
  }
  return { raw: typeof json === 'string' ? json : JSON.stringify(json, null, 2) }
}

export function fieldType(entity: AutomationEntity | undefined, field: string): AutomationFieldType | undefined {
  const base = field.startsWith(PREVIOUS) ? field.slice(PREVIOUS.length) : field
  return entity?.fields?.find((candidate) => candidate.name === base)?.type
}

function typed(text: string, type: AutomationFieldType | undefined): unknown {
  const trimmed = text.trim()
  if (type === 'number') {
    // Не число — уходит как есть: сервер откажет словами («не подходит полю»), а не мы молча.
    const number = Number(trimmed.replace(',', '.'))
    return trimmed !== '' && Number.isFinite(number) ? number : trimmed
  }
  if (type === 'boolean') return trimmed === 'true' ? true : trimmed === 'false' ? false : trimmed
  return trimmed
}

function leafJson(leaf: CondLeaf, entity: AutomationEntity | undefined): Record<string, unknown> {
  const result: Record<string, unknown> = { field: leaf.field, op: leaf.op }
  if (NO_VALUE_OPS.has(leaf.op)) return result
  const type = fieldType(entity, leaf.field)
  result.value = LIST_OPS.has(leaf.op)
    ? leaf.value.split(/[,;]/).map((part) => part.trim()).filter(Boolean).map((part) => typed(part, type))
    : typed(leaf.value, type)
  return result
}

function groupJson(group: CondGroup, entity: AutomationEntity | undefined): Record<string, unknown> {
  return {
    [group.mode]: group.items.map((item) => (item.kind === 'leaf' ? leafJson(item, entity) : groupJson(item, entity))),
  }
}

/** Черновик — в условия тела запроса. Пустая корневая группа — «всегда», `{}`. */
export function conditionsJson(draft: ConditionsDraft, entity: AutomationEntity | undefined): unknown {
  if (draft.raw !== undefined) {
    const text = draft.raw.trim()
    return text === '' ? {} : JSON.parse(text)
  }
  return draft.root.items.length === 0 ? {} : groupJson(draft.root, entity)
}

const DURATION = /^(-)?P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?)?$/

export function draftTimer(json: unknown, anchors: { name: string }[] | undefined): TimerDraft {
  const fallback: TimerDraft = { anchor: anchors?.[0]?.name ?? 'createdAt', direction: 'after', amount: '2', unit: 'H', custom: null }
  if (!isObject(json)) return fallback
  const anchor = typeof json.anchor === 'string' ? json.anchor : fallback.anchor
  const after = typeof json.after === 'string' ? json.after : ''
  const match = DURATION.exec(after)
  if (match) {
    const parts = [['D', match[2]], ['H', match[3]], ['M', match[4]]].filter(([, value]) => value != null && value !== '')
    if (parts.length === 1) {
      const [unit, amount] = parts[0] as [TimerDraft['unit'], string]
      return { anchor, direction: match[1] ? 'before' : 'after', amount, unit, custom: null }
    }
  }
  return { ...fallback, anchor, custom: after }
}

export function timerJson(draft: TimerDraft): { anchor: string; after: string } {
  if (draft.custom != null) return { anchor: draft.anchor, after: draft.custom.trim() }
  const amount = draft.amount.trim() || '0'
  const body = draft.unit === 'D' ? `P${amount}D` : `PT${amount}${draft.unit}`
  return { anchor: draft.anchor, after: `${draft.direction === 'before' ? '-' : ''}${body}` }
}

export function draftActions(json: unknown): ActionDraft[] {
  if (!Array.isArray(json)) return []
  return json.filter(isObject).map((action) => ({
    key: nextKey(),
    type: String(action.type ?? ''),
    params: Object.fromEntries(Object.entries(action).filter(([name]) => name !== 'type').map(([name, value]) => [name, valueText(value)])),
  }))
}

/**
 * Действие — в тело запроса. Пустые параметры не отправляются: «не задано» у срока задачи и
 * «срок 0 часов» — разные вещи, и пустое поле означает первое.
 */
export function actionJson(action: ActionDraft, params: AutomationActionParam[] | undefined): Record<string, unknown> {
  const result: Record<string, unknown> = { type: action.type }
  for (const [name, value] of Object.entries(action.params)) {
    if (value.trim() === '') continue
    const kind = params?.find((param) => param.name === name)?.kind
    if (kind === 'hours') {
      const number = Number(value.trim())
      result[name] = Number.isFinite(number) ? number : value.trim()
    } else {
      result[name] = kind === 'longtext' ? value : value.trim()
    }
  }
  return result
}
